import { FC, useMemo, memo, useCallback, ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { Search, XIcon, Menu } from 'lucide-react';

import { AUTH_ROUTES, APP_ROUTES } from '@navigation/types';
import { useModal } from '@hooks/useModal';
import { useDownloadAppModal } from '@hooks/useDownloadAppModal';
import { useScreenSizes } from '@hooks/useScreenSizes';
import { useAgoraEngine } from '@providers/AgoraEngineProvider';
import { useRecordedShow } from '@providers/RecordedShowProvider';
import { useSearch } from '@providers/SearchProvider';
import { useAuthUser } from '@redux/selectors/auth';
import { useCurrentUser } from '@redux/selectors/users';
import { AvatarSize } from '@typings/user';
import { TEXT_INPUT_PLACEHOLDERS } from '@utils/constants/form';
import { HEADER_HEIGHT, MOBILE_HEADER_HEIGHT } from '@utils/constants';
import Avatar from '@components/avatar';
import GoLiveButton from '@components/buttons/GoLive';
import TouchableOpacity from '@components/buttons/TouchableOpacity';
import DefaultInput from '@components/fields/input';
import ShowInProgressModal from '@components/modal/ShowInProgressModal';
import { PremierText } from '@components/text';
import logo from '@assets/images/omili-logo.png';

import { Score } from './Score';

const Container = styled.div`
  width: 100%;
  min-height: ${HEADER_HEIGHT}px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.black};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 8px;
  }
`;

const TabletContainer = styled.div`
  min-height: ${MOBILE_HEADER_HEIGHT}px;
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.black};
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 8px;
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const CenterSection = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
`;

const MenuButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  border-radius: 50%;
  background: none;
  border: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.white};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.grey[800]};
  }
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  cursor: default;
  user-select: none;
`;

const TabletLogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  cursor: default;
  user-select: none;
`;

const Logo = styled(TouchableOpacity)`
  max-width: 72px;
  max-height: 72px;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    max-width: 52px;
    max-height: 52px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const InputContainer = styled.div`
  max-width: 300px;
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0 16px;
  position: relative;
`;

const SearchInput = styled(DefaultInput)`
  padding-right: 48px;
`;

const SearchIcon = styled(Search)`
  position: absolute;
  right: 16px;
  color: ${({ theme }) => theme.colors.grey[300]};
  pointer-events: none;
`;

const CloseIcon = styled(XIcon)`
  position: absolute;
  right: 16px;
  color: ${({ theme }) => theme.colors.grey[300]};
  cursor: pointer;
`;

const GoLiveButtonContainer = styled.div`
  margin: 0 16px;
`;

const AvatarContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0 16px;
`;

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  margin-right: 8px;
`;

const ProfileTitle = styled(PremierText)`
  font-size: ${({ theme }) => theme.fonts.fontSize.xl};
  cursor: default;
  user-select: none;
`;

type Props = {
  onSideMenuCollapse?: () => void;
};

const Header: FC<Props> = ({ onSideMenuCollapse }) => {
  const { isAnonymous } = useAuthUser();
  const currentUserData = useCurrentUser();
  const { currentShowId, onFinishOrLeaveCurrentShow } = useAgoraEngine();
  const { currentRecordedShowId, closeCurrentRecordedShow } = useRecordedShow();
  const score = useMemo(
    () => Math.round(currentUserData?.score || 0),
    [currentUserData?.score],
  );
  const {
    isModalShown: isShowInProgressModalShown,
    hideModal: hideShowInProgressModal,
    showModal: showShowInProgressModal,
  } = useModal();
  const isGoLiveShown = !location.pathname.includes('show');
  const isSearchShown =
    location.pathname === APP_ROUTES.HOME ||
    location.pathname.includes(APP_ROUTES.SEARCH);
  const navigate = useNavigate();
  const { searchValue, onSearch } = useSearch();
  const { isTablet, isMobile } = useScreenSizes();
  const { openDownloadAppModal } = useDownloadAppModal();

  const onGoLive = useCallback(() => {
    openDownloadAppModal();
  }, [openDownloadAppModal]);

  const onAvatarClick = useCallback(() => {
    if (isAnonymous && !currentUserData?.id) {
      if (currentShowId || currentRecordedShowId) {
        return showShowInProgressModal();
      }
      return navigate(AUTH_ROUTES.LOGIN);
    }
    return navigate(`${APP_ROUTES.PROFILE}/${currentUserData?.id}`);
  }, [
    isAnonymous,
    currentUserData?.id,
    navigate,
    showShowInProgressModal,
    currentShowId,
    currentRecordedShowId,
  ]);

  const onExitShowConfirm = useCallback(() => {
    if (currentShowId) {
      onFinishOrLeaveCurrentShow();
    }
    if (currentRecordedShowId) {
      closeCurrentRecordedShow();
    }
    hideShowInProgressModal();
    if (location.pathname.includes('show')) {
      navigate(AUTH_ROUTES.LOGIN);
    }
  }, [
    currentShowId,
    currentRecordedShowId,
    onFinishOrLeaveCurrentShow,
    closeCurrentRecordedShow,
    hideShowInProgressModal,
    navigate,
  ]);

  const onSearchInputClick = useCallback(() => {
    if (!isAnonymous && currentUserData?.id) {
      navigate(APP_ROUTES.SEARCH);
    }
  }, [isAnonymous, currentUserData?.id, navigate]);

  const onMenuButtonClick = useCallback(() => {
    if (onSideMenuCollapse) {
      onSideMenuCollapse();
    }
  }, [onSideMenuCollapse]);

  if (isTablet || isMobile) {
    return (
      <TabletContainer>
        <LeftSection>
          <MenuButton onClick={onMenuButtonClick}>
            <Menu size={24} />
          </MenuButton>
        </LeftSection>

        <CenterSection>
          <TabletLogoContainer>
            <Logo disabled>
              <Image src={logo} alt='logo' />
            </Logo>
          </TabletLogoContainer>
        </CenterSection>

        <RightContainer />
      </TabletContainer>
    );
  }

  return (
    <>
      <Container>
        <LogoContainer>
          <Logo onClick={() => navigate(APP_ROUTES.HOME)}>
            <Image src={logo} alt='logo' />
          </Logo>
        </LogoContainer>

        <RightContainer>
          {isSearchShown && (
            <InputContainer>
              <SearchInput
                placeholder={TEXT_INPUT_PLACEHOLDERS.SEARCH}
                value={searchValue}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onSearch(event.target.value)
                }
                onClick={onSearchInputClick}
                isSkewed
              />

              {searchValue ? (
                <CloseIcon size={20} onClick={() => onSearch('')} />
              ) : (
                <SearchIcon size={20} />
              )}
            </InputContainer>
          )}

          {isGoLiveShown && (
            <GoLiveButtonContainer>
              <GoLiveButton onClick={onGoLive} />
            </GoLiveButtonContainer>
          )}

          <AvatarContainer>
            {!!currentUserData?.id && (
              <InfoContainer>
                <ProfileTitle>My Profile</ProfileTitle>

                <Score data-text={String(score)}>{score}</Score>
              </InfoContainer>
            )}

            <Avatar
              userId={currentUserData?.id}
              size={AvatarSize.Large}
              onClick={onAvatarClick}
            />
          </AvatarContainer>
        </RightContainer>
      </Container>

      <ShowInProgressModal
        isShown={isShowInProgressModalShown}
        onClose={hideShowInProgressModal}
        onConfirm={onExitShowConfirm}
      />
    </>
  );
};

export default memo(Header);
